/**
 * CustomConfiguration.js – Migrated from admin4.0 CustomConfig.js (subset).
 * Flows: Auto log out time, Transaction Number Mandatory, Should the bill amount be auto-fetched, ₹2,00,000 Cash Limit per Patient.
 */
import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Paper,
  Button,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import { setCachevalue } from "../../Utility/Services/CacheProviderService";
import Loader from "../../Components/Loader";
import "../../Styles/Configuration.css";

/** Config options: type 'number' = stepper, type 'checkbox' = checkbox. hidden: true to hide row. */
const CONFIG_ROWS = [
  { id: "logoutTime", label: "Auto log out time (minutes)", type: "number", min: 10, max: 60, step: 5 },
  { id: "is_transaction_number_mandatory", label: "Do the system requires a Transaction Number Mandatory", type: "checkbox" },
  { id: "auto_fetch_bill_amount_to_payment", label: "Should the bill amount be auto-fetched", type: "checkbox" },
  // { id: "two_lakh_cash_condition", label: "Enable ₹2,00,000 Cash Limit per Patient", type: "checkbox", hidden: true },
];

export default function CustomConfiguration() {
  const [logoutTime, setLogoutTime] = useState(15);
  const [is_transaction_number_mandatory, setIsTransactionNumberMandatory] = useState(false);
  const [auto_fetch_bill_amount_to_payment, setAutoFetchBillAmountToPayment] = useState(false);
  const [two_lakh_cash_condition, setTwoLakhCashCondition] = useState(false);
  const [isLoader, setIsLoader] = useState(false);
  const [disableBtn, setDisableBtn] = useState(false);
  const [errorMsg, setErrorMsg] = useState(false);
  const [errorMsgText, setErrorMsgText] = useState("");
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState("");

  const optionStateMap = {
    logoutTime: [logoutTime, setLogoutTime],
    is_transaction_number_mandatory: [is_transaction_number_mandatory, setIsTransactionNumberMandatory],
    auto_fetch_bill_amount_to_payment: [auto_fetch_bill_amount_to_payment, setAutoFetchBillAmountToPayment],
    two_lakh_cash_condition: [two_lakh_cash_condition, setTwoLakhCashCondition],
  };

  const errorMessage = useCallback((msg) => {
    setErrorMsgText(msg);
    setErrorMsg(true);
  }, []);
  const successMessage = useCallback((msg) => {
    setSuccessMsgText(msg);
    setSuccessMsg(true);
  }, []);
  const msgClose = useCallback(() => {
    setErrorMsg(false);
    setSuccessMsg(false);
  }, []);

  const getCustomConfig = useCallback(() => {
    setIsLoader(true);
    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_CUSTOM_CONFIG)
      .then((response) => {
        if (response?.data?.status === "success") {
          const d = response.data?.data || {};
          setLogoutTime(d.auto_logout_minutes ?? 15);
          setIsTransactionNumberMandatory(!!d.transaction_number_mandatory);
          setAutoFetchBillAmountToPayment(!!d.bill_amount_auto_fetched);
          setTwoLakhCashCondition(!!d.enable_cash_limit_per_patient);
        }
        setIsLoader(false);
      })
      .catch((e) => {
        setIsLoader(false);
        errorMessage(e?.response?.data?.message || e?.message);
      });
  }, [errorMessage]);

  useEffect(() => {
    getCustomConfig();
  }, [getCustomConfig]);

  const handleLogoutTimeChange = (type) => {
    if (type === "increment" && logoutTime < 60) setLogoutTime((prev) => Math.min(60, prev + 5));
    if (type === "decrement" && logoutTime > 10) setLogoutTime((prev) => Math.max(10, prev - 5));
  };

  const renderOptionValue = (row) => {
    const [value, setValue] = optionStateMap[row.id] ?? [];
    if (row.type === "number") {
      return (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleLogoutTimeChange("decrement")}
            disabled={value <= (row.min ?? 0)}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ minWidth: 40, textAlign: "center" }}>{value}</Typography>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleLogoutTimeChange("increment")}
            disabled={value >= (row.max ?? 60)}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Stack>
      );
    }
    if (row.type === "checkbox") {
      return (
        <FormControlLabel
          control={
            <Checkbox checked={!!value} onChange={(e) => setValue(e.target.checked)} size="small" />
          }
          label={value ? "Yes" : "No"}
        />
      );
    }
    return null;
  };

  const updateCustomDetails = () => {
    setDisableBtn(true);
    const payload = {
      auto_logout_minutes: logoutTime ?? 15,
      transaction_number_mandatory: is_transaction_number_mandatory,
      bill_amount_auto_fetched: auto_fetch_bill_amount_to_payment,
      enable_cash_limit_per_patient: two_lakh_cash_condition,
    };
    RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_CUSTOM_CONFIG)
      .then((response) => {
        if (response?.data?.status === "success") {
          successMessage(response.data.message || "Custom config saved successfully");
          getCustomConfig();
          setCachevalue(String(logoutTime ?? 15), "auto_log_out_time");
          setCachevalue(String(is_transaction_number_mandatory), "is_transaction_number_mandatory");
          setCachevalue(String(auto_fetch_bill_amount_to_payment), "auto_fetch_bill_amount_to_payment");
        }
        setDisableBtn(false);
      })
      .catch((e) => {
        setDisableBtn(false);
        errorMessage(e?.response?.data?.message || e?.message);
      });
  };

  return (
    <Box component="div" className="eMed_refdoc_container" sx={{ width: "100%", boxSizing: "border-box" }}>
      <Paper elevation={1} sx={{ p: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F5F8F7" }}>
                <TableCell sx={{ fontWeight: 600 }}>Configuration</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {CONFIG_ROWS.filter((row) => !row.hidden).map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell>{renderOptionValue(row)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: "center" }}>
          <Button variant="contained" size="small" disabled={disableBtn} onClick={updateCustomDetails}>
            Save
          </Button>
        </Stack>
      </Paper>
      {errorMsg && <ToastMsg severity="error" msg={errorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      <Loader loaderOpen={isLoader} />
    </Box>
  );
}
